import React, { useState, useEffect } from 'react';

// Icon name to file path mapping (in public/icons folder)
const iconPaths = {
  'sidebar_home': '/icons/sidebar_home.svg',
  'sidebar_overview': '/icons/sidebar_overview.svg',
  'sidebar_activity': '/icons/sidebar_activity.svg',
  'sidebar_console': '/icons/sidebar_console.svg',
  'dashboard': '/icons/dashboard.svg',
  'sidebar_pinned': '/icons/sidebar_pinned.svg',
  'sidebar_supporticon': '/icons/sidebar_supporticon.svg',
  'sidebar_docs': '/icons/sidebar_docs.svg',
  'sidebar_changelog': '/icons/sidebar_changelog.svg',
  'sidebar_support': '/icons/sidebar_support.svg',
  'sidebar_contactsales': '/icons/sidebar_contactsales.svg',
  'sidebar_settings': '/icons/sidebar_settings.svg',
  'sidebar_usersettings': '/icons/sidebar_usersettings.svg',
  'sidebar_apps': '/icons/sidebar_apps.svg',
  'sidebar_smartcontract': '/icons/sidebar_smartcontract.svg',
  'sidebar_logout': '/icons/sidebar_logout.svg',
  'sidebar_collapsesidebar': '/icons/sidebar_collapsesidebar.svg',
  'calendar': '/icons/calendar.svg',
  'edit_pencil': '/icons/edit_pencil.svg',
  'add_plus': '/icons/add_plus.svg',
  'save': '/icons/save.svg',
  'filter': '/icons/filter.svg',
  'linechart': '/icons/linechart.svg',
  'stacked_linechart': '/icons/stacked_linechart.svg',
  'barchart': '/icons/barchart.svg',
  'stacked_barchart': '/icons/stacked_barchart.svg',
  'piechart': '/icons/piechart.svg',
  'table': '/icons/table.svg',
  'metric': '/icons/metric.svg',
  'analytics': '/icons/analytics.svg',
  'funnel': '/icons/funnel.svg',
  'flows': '/icons/flows.svg',
  'retention': '/icons/retention.svg',
  'share': '/icons/share.svg',
  'text': '/icons/text.svg',
  'media': '/icons/media.svg',
  'delete': '/icons/delete.svg',
  'website': '/icons/website.svg',
  'smartcontract': '/icons/smartcontract.svg',
};

/**
 * Reusable Icon component that loads SVG icons from the public/icons folder
 * @param {string} name - Name of the icon (e.g., 'sidebar_home')
 * @param {number} size - Size of the icon in pixels (default: 24)
 * @param {string} color - Color of the icon (default: 'currentColor')
 * @param {object} style - Additional inline styles
 * @param {string} className - Additional CSS classes
 */
const Icon = ({ name, size = 24, color = 'currentColor', style = {}, className = '', ...props }) => {
  const [svgContent, setSvgContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const iconPath = iconPaths[name];
    
    if (!iconPath) {
      console.warn(`Icon "${name}" not found in iconPaths`);
      setLoading(false);
      return;
    }

    // Fetch the SVG content from the public folder
    fetch(iconPath)
      .then(res => {
        if (!res.ok) {
          throw new Error(`Failed to fetch ${iconPath}`);
        }
        return res.text();
      })
      .then(text => {
        setSvgContent(text);
        setLoading(false);
      })
      .catch(err => {
        console.error(`Failed to load icon: ${name}`, err);
        setLoading(false);
      });
  }, [name]);

  const iconPath = iconPaths[name];

  if (!iconPath) {
    return null;
  }

  // If we have SVG content, render it inline for color control
  if (svgContent && !loading) {
    try {
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
      const svgElement = svgDoc.documentElement;
      
      // Check for parsing errors
      const parserError = svgDoc.querySelector('parsererror');
      if (parserError) {
        throw new Error('SVG parsing failed');
      }
      
      // Update attributes
      svgElement.setAttribute('width', size);
      svgElement.setAttribute('height', size);
      svgElement.removeAttribute('fill');
      
      // Update fill in all paths
      const paths = svgElement.querySelectorAll('path');
      paths.forEach(path => {
        const currentFill = path.getAttribute('fill');
        if (currentFill && currentFill !== 'none') {
          path.setAttribute('fill', color === 'currentColor' ? 'currentColor' : color);
        }
      });

      // Apply style
      const styleAttr = Object.entries(style)
        .map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${v}`)
        .join('; ');
      if (styleAttr) {
        svgElement.setAttribute('style', styleAttr);
      }
      
      if (className) {
        svgElement.setAttribute('class', className);
      }

      return (
        <span
          style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: color !== 'currentColor' ? color : undefined,
            width: size,
            height: size,
            ...style 
          }}
          className={className}
          {...props}
          dangerouslySetInnerHTML={{ __html: svgElement.outerHTML }}
        />
      );
    } catch (error) {
      console.error(`Error rendering SVG for icon: ${name}`, error);
    }
  }

  // Fallback: use img tag while loading or if fetch fails
  return (
    <img
      src={iconPath}
      alt={name}
      width={size}
      height={size}
      style={{
        display: 'inline-block',
        ...style
      }}
      className={className}
      {...props}
    />
  );
};

export default Icon;
